/**
 * Pure helper functions and constants extracted from claim-detail-page.tsx.
 *
 * These have no React component state or hook dependencies and can be
 * imported by the detail page (or tests) without pulling in the full
 * component tree.
 */

import type { ReactNode } from "react";
import type { TFunction } from "i18next";
import type {
  ActionFieldMeta,
  PickedValue,
  RegistrationProductPick,
} from "@/admin/hooks/use-claims";
import type { CardFieldItem } from "@/admin/components/shared/editable-card-types";
import type { FormFieldMeta, FieldType } from "@/shared/lib/forms";
import {
  defineField,
  findOptionLabel,
  normaliseClaimField,
} from "@/shared/lib/forms";
import {
  isEntityFileObject,
  entityFileDisplayUrl,
  entityFileDisplayName,
} from "@/admin/lib/entity-card-display";
import { isAddressLike, addressDisplay } from "@/shared/lib/formatting/address";
import {
  formatCurrencyValue,
  formatDurationFromMonths,
  isMoneyObject,
} from "@/admin/lib/formatting";
import { DEFAULT_CURRENCY } from "@/shared/lib/formatting";
import { titleCaseKey } from "@/shared/lib/humanise";

// ── Constants ──────────────────────────────────────────

export const STATUS_WORKFLOW_KEY = "lifecycle_status";

export const DECISION_FIELDS = new Set([
  "decision_reason",
  "outcome",
  "estimated_amount",
  "approved_amount",
  "goodwill_cost",
  "currency",
  "assessed_component",
  "assessed_fault",
  "coverage_determination",
  "approval_reason",
  "goodwill",
  "close_reason",
]);

export const WARRANTY_TYPE_LABELS: Record<string, string> = {
  MANUFACTURER_WARRANTY: "Standard",
  RETAILER_WARRANTY: "Retailer",
  EXTENDED_WARRANTY: "Extended",
};

/** Preferred display order for decision fields - reason/outcome first, then amounts, then assessment, then closure */
export const DECISION_ORDER = [
  "decision_reason",
  "outcome",
  "estimated_amount",
  "approved_amount",
  "goodwill_cost",
  "currency",
  "coverage_determination",
  "approval_reason",
  "assessed_component",
  "assessed_fault",
  "goodwill",
  "close_reason",
];

/** Fields to push to the bottom of claim details (evidence last) */
export const CLAIM_BOTTOM_FIELDS = new Set(["photos"]);

/**
 * Preferred top-of-card order for the Claim Details card. Listed fields
 * appear first in this order; everything else keeps its existing order
 * beneath them (CLAIM_BOTTOM_FIELDS still sink to the bottom). The raw
 * attribute order is otherwise the value-save order, which isn't
 * meaningful to a reader - this gives the key fields a stable position.
 */
export const CLAIM_DETAIL_ORDER = ["date_of_incident", "fault", "description"];

/**
 * Supplier / contractor / property fields that the SupplierInfoCard on
 * the Overview tab reads from.  Filtered out of the Claim Details card
 * for holdingless claims so information isn't duplicated.
 */
export const SUPPLIER_CARD_FIELDS = new Set([
  "supplier_name",
  "supplier_contact_name",
  "supplier_contact_first_name",
  "supplier_contact_last_name",
  "supplier_contact_email",
  "supplier_email",
  "supplier_contact_phone",
  "supplier_phone",
  "asm_name",
  "asm_first_name",
  "asm_last_name",
  "contractor_name",
  "contractor_contact_name",
  "contractor_phone",
  "contractor_email",
  "property_address_line_1",
  "property_city",
  "property_state",
  "property_zip",
]);

/**
 * End-consumer (end-user) fields rendered by the Consumer card, kept
 * distinct from the holding's registered owner (which may be a B2B
 * account/dealer). Filtered out of the generic Claim Details card so the
 * information isn't duplicated. Generic — any tenant that captures these
 * attributes on a claim gets the Consumer card; tenants that don't are
 * unaffected (the card only renders when at least one value is present).
 */
export const CONSUMER_CARD_FIELDS = new Set([
  "end_user_name",
  "end_user_first_name",
  "end_user_last_name",
  "end_user_email",
  "end_user_phone",
  "end_user_address",
]);

/**
 * Claim-level product fields surfaced on the Product card (e.g. the
 * serial number of the specific unit being claimed/recalled) rather than
 * in the generic Claim Details list. Filtered out of Claim Details.
 */
export const PRODUCT_CLAIM_FIELDS = new Set(["serial_number"]);

/**
 * Code names the schema has tagged as hidden from the generic Claim Details
 * card, via ``configuration_data.hidden_from_claim_details``.
 *
 * WPS-102 AC6: an attribute can be retired from the card without being deleted
 * from the schema - OC stopped asking the claimant for `quantity_impacted`
 * (the lab's Quantity Approved is now the only quantity that advises payment)
 * but claims filed before the change still carry a value, and deleting the
 * attribute would drop it from the API too. The tag is what stops that stored
 * value resurfacing on the card.
 *
 * Config-driven and tenant-agnostic: whatever the schema tags is hidden, and
 * no attribute name is hardcoded here.
 */
export function hiddenFromClaimDetailsCodes(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): Set<string> {
  const hidden = new Set<string>();
  for (const field of fields ?? []) {
    const config = field.configuration_data as
      Record<string, unknown> | undefined;
    if (config?.hidden_from_claim_details === true) {
      hidden.add(field.code_name);
    }
  }
  return hidden;
}

/**
 * Codenames the given fields declare as ``configuration_data.update_permission``,
 * the grant for changing them once the claim exists (WPS-204). Sorted, unique.
 */
export function fieldUpdatePermissions(
  codes: Iterable<string>,
  metadata: ReadonlyMap<string, { configuration_data?: unknown }>,
): string[] {
  const grants = new Set<string>();
  for (const code of codes) {
    const config = metadata.get(code)?.configuration_data as
      Record<string, unknown> | undefined;
    const grant = config?.update_permission;
    if (typeof grant === "string" && grant) grants.add(grant);
  }
  return [...grants].sort();
}

/**
 * Edit config for a card whose fields may declare update grants: none keeps the
 * claim-level config; otherwise holders of every grant edit at any stage.
 */
export function grantScopedEditConfig<T>(
  grants: readonly string[],
  can: (codename: string) => boolean,
  claimLevel: T | undefined,
  anyStage: T | undefined,
): T | undefined {
  if (grants.length === 0) return claimLevel;
  return grants.every((grant) => can(grant)) ? anyStage : undefined;
}

/**
 * Resolve the code_name the schema tags with a given semantic role.
 *
 * Panels that need "the lab's verdict" or "the approved quantity" ask for the
 * role rather than naming an attribute, so a tenant whose lab fields are named
 * differently still gets the right value. Declared as
 * ``configuration_data.semantic_role`` on the attribute.
 */
export function codeForSemanticRole(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
  role: string,
): string | null {
  for (const field of fields ?? []) {
    const config = field.configuration_data as
      Record<string, unknown> | undefined;
    if (config?.semantic_role === role) return field.code_name;
  }
  return null;
}

/**
 * Code names the schema marks read-only on the Overview Defect Details card,
 * via ``configuration_data.read_only_on_defect_details``.
 *
 * Same contract as ``hiddenFromClaimDetailsCodes``: the row still renders with
 * whatever is stored, it just never reaches the edit popup. Declared rather
 * than matched, so the card names no attribute.
 */
export function readOnlyOnDefectDetailsCodes(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): Set<string> {
  const readOnly = new Set<string>();
  for (const field of fields ?? []) {
    const config = field.configuration_data as
      Record<string, unknown> | undefined;
    if (config?.read_only_on_defect_details === true) {
      readOnly.add(field.code_name);
    }
  }
  return readOnly;
}

/** One field on the lab's findings card, as the schema declares it. */
export type LabCardField = {
  code_name: string;
  /** Must be filled before the card can be saved. */
  required: boolean;
  /** Shown as context the lab reads, never edits. */
  readOnly: boolean;
  /** Omit the read-only row entirely when there is no value. */
  hideWhenEmpty: boolean;
};

/**
 * The lab findings card, composed from the schema rather than a constant.
 *
 * Each attribute opts in with ``configuration_data.lab_card``:
 *
 * ```json
 * "lab_card": { "order": 1, "required": true }
 * "lab_card": { "order": 5, "read_only": true, "hide_when_empty": true }
 * ```
 *
 * Order, required-ness, read-only-ness and empty-row suppression all come from
 * there, so the card is whatever a tenant's schema says it is - a different
 * tenant's lab card is different tags, not different code. This replaces the
 * hardcoded field-order / required / read-only lists this card used to carry.
 *
 * ``required`` is applied on the card only. Schema-level ``required`` is
 * validated against the WHOLE schema on every write, so marking a lab field
 * required there makes claim creation impossible.
 */
export function labCardFields(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): LabCardField[] {
  return taggedCardFields(fields, "lab_card");
}

/** Fields tagged with ``configuration_data[tag]``, in the tag's ``order``. */
function taggedCardFields(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
  tag: string,
): LabCardField[] {
  const specs: { field: LabCardField; order: number }[] = [];
  for (const field of fields ?? []) {
    const config = field.configuration_data as
      Record<string, unknown> | undefined;
    const spec = config?.[tag];
    if (!spec || typeof spec !== "object") continue;
    const s = spec as Record<string, unknown>;
    specs.push({
      order: typeof s.order === "number" ? s.order : 0,
      field: {
        code_name: field.code_name,
        required: s.required === true,
        readOnly: s.read_only === true,
        hideWhenEmpty: s.hide_when_empty === true,
      },
    });
  }
  return specs.sort((a, b) => a.order - b.order).map((s) => s.field);
}

/**
 * The Lab-tab "Defect Details" card (WPS-165, formerly "Defect Classification"),
 * composed from the schema rather than a hardcoded frontend `Set`.
 *
 * Each attribute opts in with ``configuration_data.defect_card``, exactly like
 * ``lab_card`` above (``order`` / ``required`` / ``read_only`` /
 * ``hide_when_empty``). This is what lets a tenant put Plant / Minor Defect
 * Category / Product Impacted on the card - and keep them editable during Lab
 * Analysis - purely by tagging the attribute, not by editing this component.
 */
export function defectCardFields(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): LabCardField[] {
  return taggedCardFields(fields, "defect_card");
}

/**
 * WPS-258: a card that lists the product also lists its colour, right after
 * it - the two name one catalogue product - so a schema need not tag both.
 */
export function withColourBesideProduct(
  specs: LabCardField[],
  productCode: string | null,
  colourCode: string | null,
): LabCardField[] {
  if (!productCode || !colourCode) return specs;
  if (specs.some((spec) => spec.code_name === colourCode)) return specs;
  const at = specs.findIndex((spec) => spec.code_name === productCode);
  if (at < 0) return specs;
  const colour: LabCardField = {
    code_name: colourCode,
    required: false,
    readOnly: specs[at].readOnly,
    hideWhenEmpty: false,
  };
  return [...specs.slice(0, at + 1), colour, ...specs.slice(at + 1)];
}

/** The catalogue id a picked value carries; "" for anything else. */
export function pickedUuid(value: unknown): string {
  if (value && typeof value === "object" && "uuid" in value) {
    const uuid = (value as { uuid?: unknown }).uuid;
    return typeof uuid === "string" ? uuid : "";
  }
  return "";
}

/**
 * WPS-258: a Product / Colour field the claim has not set yet starts from its
 * registration. The card shows the registered value and the edit popup opens
 * on it as a real picker value, so saving keeps it. The colour only follows
 * while the claim names no other product, or it would be another product's.
 */
export function startFromRegistration(
  item: CardFieldItem,
  slots: { product: string | null; colour: string | null },
  pick: RegistrationProductPick | null | undefined,
  claimProductUuid: string,
): CardFieldItem {
  const blank =
    item.value == null ||
    (typeof item.value === "string" && item.value.trim() === "") ||
    (typeof item.value === "object" && !pickedUuid(item.value));
  if (!pick || !blank) return item;
  const code = item.field.code_name;
  let start: PickedValue | null = null;
  if (code === slots.product) {
    start = pick.product;
  } else if (
    code === slots.colour &&
    (!claimProductUuid || claimProductUuid === pick.product?.uuid)
  ) {
    start = pick.colour;
  }
  return start ? { ...item, displayValue: start.name, editValue: start } : item;
}

/**
 * The Overview Supplier card (WPS-204 #7), composed from the schema.
 *
 * Each attribute opts in with ``configuration_data.supplier_card`` (same keys
 * as ``lab_card``), so any claim type whose schema tags supplier fields gets
 * the card. Retired (``deprecated``) attributes never render on it.
 */
export function supplierCardFields(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): LabCardField[] {
  const live = (fields ?? []).filter(
    (field) =>
      !(field.configuration_data as Record<string, unknown> | undefined)
        ?.deprecated,
  );
  return taggedCardFields(live, "supplier_card");
}

/**
 * Code names the schema pins to the top of the Claim Details card, via
 * ``configuration_data.pin_to_claim_details_top``.
 *
 * WPS-102 AC5: the card previews only the first few fields and collapses the
 * rest behind "Show N more". The lab's decision sorts late, so at Material &
 * Labor Review the approved quantity landed under the fold - present in the
 * payload, invisible to the person approving payment, which defeats the point
 * of surfacing it. Pinned fields keep their relative order among themselves.
 *
 * Config-driven and tenant-agnostic, same contract as
 * `hiddenFromClaimDetailsCodes`: whatever the schema tags is pinned.
 */
export function pinnedClaimDetailCodes(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
): Set<string> {
  const pinned = new Set<string>();
  for (const field of fields ?? []) {
    const config = field.configuration_data as
      Record<string, unknown> | undefined;
    if (config?.pin_to_claim_details_top === true) {
      pinned.add(field.code_name);
    }
  }
  return pinned;
}

/**
 * The lab's approved quantity, rendered for payment review (WPS-102 AC5).
 *
 * Full / Partial on its own, or "Partial - 12 sq" when the lab recorded a
 * square count. Returns "-" until the lab has made the call, so the review
 * surface never implies an approval nobody granted.
 */
export function quantityApprovedLabel(
  coverage: unknown,
  squareCount: unknown,
): string {
  if (coverage === null || coverage === undefined || coverage === "")
    return "-";
  const parts = [humanise(String(coverage))];
  if (squareCount !== null && squareCount !== undefined && squareCount !== "") {
    parts.push(`${String(squareCount)} sq`);
  }
  return parts.join(" - ");
}

/**
 * The label a choice field's option list gives a stored value, searching both
 * ``configuration_data.options`` and nested ``option_groups``. ``null`` when
 * the field or option is not found, so callers can fall back.
 */
export function optionLabelFor(
  fields: { code_name: string; configuration_data?: unknown }[] | undefined,
  codeName: string,
  value: unknown,
): string | null {
  if (typeof value !== "string" || !value) return null;
  const field = (fields ?? []).find((f) => f.code_name === codeName);
  return findOptionLabel(field?.configuration_data, value);
}

/**
 * WPS-215 AC3: "Quantity Impacted, based on actual squares included in the
 * settlement" - for the LOA Review card.
 *
 * Sums the LABOR lines' quantities, grouped by each line's own UOM, so the
 * unit shown is the tenant's data rather than a hardcoded "sq". MATERIAL and
 * OTHER lines are excluded: their quantities are bundles, rolls or a fee at
 * quantity 1. Mixed UOMs are listed per unit, never added together.
 *
 * Every labour line is summed: two labour lines on the same area (say a
 * remove-and-replace plus a hand-seal) count twice. There is no generic rule
 * to tell overlapping work from separate areas, so none is guessed.
 *
 * Returns ``"-"`` when the settlement prices no labour.
 */
export function settlementQuantityImpactedLabel(
  lineItems:
    | { category?: string | null; quantity?: unknown; uom?: string | null }[]
    | undefined,
  locale: string,
): string {
  const byUom = new Map<string, number>();
  for (const line of lineItems ?? []) {
    if (line.category !== "LABOR") continue;
    const quantity = Number(line.quantity);
    // A malformed or absent quantity contributes nothing rather than NaN-ing
    // the whole total - one bad row must not blank the field.
    if (line.quantity === null || !Number.isFinite(quantity)) continue;
    const uom = (line.uom ?? "").trim();
    byUom.set(uom, (byUom.get(uom) ?? 0) + quantity);
  }
  const format = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const parts = [...byUom.entries()]
    .filter(([, total]) => total > 0)
    .map(([uom, total]) => (uom ? `${format.format(total)} ${uom}` : format.format(total)));
  return parts.length ? parts.join(", ") : "-";
}

/** Fields that hold monetary values and should be formatted with currency */
export const CURRENCY_FIELDS = new Set([
  "estimated_amount",
  "approved_amount",
  "goodwill_cost",
  "cost",
  "price",
  "total",
  "amount",
]);

/** Fields that hold date values and should use DatePicker in edit mode */
export const DATE_FIELDS = new Set([
  "date_of_incident",
  "date_of_loss",
  "incident_date",
  "loss_date",
]);

/** Fields that hold file/image values */
export const FILE_FIELDS = new Set([
  "photos",
  "photo",
  "evidence",
  "image",
  "images",
  "document",
  "attachment",
]);

export const SENDER_ROLE_LABELS: Record<string, string> = {
  consumer: "Customer",
  dealer: "Dealer",
  tenant: "Admin",
  staff: "Staff",
  system: "System",
};

// ── Pure helpers ───────────────────────────────────────

export function humanise(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(
      /\w\S*/g,
      (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
    );
}

export function durationLabel(warranty: {
  duration?: string | null;
  duration_in_months?: number | null;
  is_lifetime?: boolean | null;
}) {
  if (warranty.is_lifetime) return "Lifetime";
  if (warranty.duration) return warranty.duration;
  return formatDurationFromMonths(warranty.duration_in_months) || "-";
}

export function deriveRecordStatus(
  holdingStatus: string | undefined,
  warrantyStatus: string | undefined,
): string {
  if (holdingStatus === "cancelled") return "Cancelled";
  if (holdingStatus === "transferred") return "Transferred";
  return humanise(warrantyStatus) || humanise(holdingStatus) || "Active";
}

/**
 * Pull the leading SKU token from a "SKU - Name" product display string.
 * Products are cached as "{SKU} - {name}" (em-dash separator), but a claim
 * title only wants the short SKU, not the full descriptive name (which can
 * be long, e.g. "AIQ-MISC - AQUACON INTERNATIONAL - MISC PRODUCT"). Falls
 * back to the whole string when there's no em-dash separator, so tenants
 * whose product names aren't SKU-prefixed are unaffected.
 */
export function productSkuFromDisplayName(
  name: string | null | undefined,
): string {
  if (!name) return "";
  const [sku] = name.split(" - ");
  return (sku ?? name).trim();
}

/**
 * WPS-203 / WPS-146 #3: the claim's product the way a person names it - the
 * product LINE and its colourway together ("Duration - Brownwood"), not the
 * colour on its own.
 *
 * A tenant that names variants by colour alone stores the colour in
 * ``product_short_name`` and the line in ``product_line_name``; a tenant whose
 * product is not a variant has no line at all, and the single name is the
 * whole answer. Both inputs come from the serializer, so this never names a
 * tenant and never assumes which of the two is populated.
 */
export function claimProductDisplayName(
  lineName: string | null | undefined,
  variantName: string | null | undefined,
): string {
  const line = (lineName ?? "").trim();
  const variant = productSkuFromDisplayName(variantName);
  // A line that merely repeats the variant adds nothing - show it once.
  if (line && variant && line.toLowerCase() !== variant.toLowerCase()) {
    return `${line} - ${variant}`;
  }
  return line || variant;
}

/**
 * The product without its variant (a colour, a size): the line first, else the
 * product's own name when it has no parent, else what the claim captured.
 */
export function productNameWithoutVariant(
  claim: {
    product_line_name?: string | null;
    product_short_name?: string | null;
    product_name?: string | null;
  },
  capturedProduct: string,
): string {
  return (
    claim.product_line_name ||
    claim.product_short_name ||
    claim.product_name ||
    capturedProduct ||
    ""
  );
}

/** Labels a tenant's Product card gives its colour and quantity rows. */
export const COLOUR_ROW_LABEL = /colou?r/i;
export const QUANTITY_ROW_LABEL = /square|quantity/i;
/** The registration card's platform column for the product's own name. */
export const PRODUCT_NAME_COLUMN = "relationship:product:product:name";

/** What the Overview's Defect Details card shows itself. */
export interface DefectDetailsShows {
  product: boolean;
  colour: boolean;
  quantity: boolean;
}

/**
 * The Product card's rows without the ones Defect Details already shows (the
 * product, its colour, its quantity), so the Overview never prints one twice.
 */
export function productRowsBesideDefectDetails<
  T extends { key: string; label: string },
>(rows: T[], shows: DefectDetailsShows): T[] {
  return rows.filter(
    (row) =>
      !(shows.product && row.key === PRODUCT_NAME_COLUMN) &&
      !(shows.colour && COLOUR_ROW_LABEL.test(row.label)) &&
      !(shows.quantity && QUANTITY_ROW_LABEL.test(row.label)),
  );
}

/** True when a card row holds a value (card values render empty as "-"). */
export function cardRowHasValue(value: unknown): boolean {
  if (value == null) return false;
  const text = String(value).trim();
  return text !== "" && text !== "-";
}

/**
 * Infer FieldType from claim attribute key/value when metadata is unavailable.
 */
export function inferFieldType(key: string, value: unknown): FieldType {
  if (CURRENCY_FIELDS.has(key)) return "MONEY";
  if (DATE_FIELDS.has(key) || key.includes("date")) return "DATE";
  if (
    FILE_FIELDS.has(key) ||
    (typeof value === "object" &&
      value !== null &&
      "file_url" in (value as Record<string, unknown>))
  )
    return "FILE";
  if (typeof value === "boolean") return "BOOLEAN";
  if (key.includes("address") || isAddressLike(value)) return "ADDRESS";
  if (looksLikeMoney(value)) return "MONEY";
  return "TEXT";
}

function looksLikeMoney(value: unknown): boolean {
  if (isMoneyObject(value)) return true;
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      return isMoneyObject(JSON.parse(value));
    } catch {
      return false;
    }
  }
  return false;
}

/** A file the backend has resolved into `{ file_url, file_name, ... }`. */
function isResolvedFile(value: unknown): value is { file_name?: string } {
  return typeof value === "object" && value !== null && "file_url" in value;
}

/**
 * A `{ display, value }` envelope: the backend pairs a raw value with a
 * ready-made label. File attributes arrive this way - single files as
 * "Screenshot.png - 272 KB", multi-file fields as "2 files".
 */
function isDisplayEnvelope(
  value: unknown,
): value is { display?: unknown; value?: unknown } {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    "display" in value &&
    "value" in value
  );
}

/**
 * Filename if the resolver supplied one, otherwise a neutral marker.
 *
 * ``t`` is threaded in rather than imported: this module is a pure helper, and
 * the house pattern is to pass the caller's translator (see ``kindLabel`` in
 * the communications activity page).
 */
function fileLabel(file: { file_name?: string }, t?: TFunction): string {
  if (file.file_name) return file.file_name;
  return t
    ? t("claims.attached", { defaultValue: "✅ Attached" })
    : "✅ Attached";
}

export function formatAttrValue(
  value: unknown,
  locale: string,
  key?: string,
  currencyFmt?: (n: number) => string,
  t?: TFunction,
): string {
  if (value === null || value === undefined) return "-";

  // Unwrap a { display, value } envelope. Money is handled by the rules below so
  // it resolves its own currency rather than trusting a pre-rendered string;
  // everything else prefers the backend's label, which is authored for the
  // surface (a filename with its size, or "2 files" for a multi-file field).
  // Without this a file envelope stringified to "[object Object]" (TA-25).
  if (isDisplayEnvelope(value)) {
    if (isMoneyObject(value.value)) {
      return formatAttrValue(value.value, locale, key, currencyFmt, t);
    }
    const display =
      typeof value.display === "string" ? value.display.trim() : "";
    if (display) return display;
    return formatAttrValue(value.value ?? null, locale, key, currencyFmt, t);
  }

  // Money, resolved in this order (TA-25):
  //
  //   1. the currency the amount was RECORDED in. An organisation may trade in
  //      several (Teknor: USD + CAD), so forcing its default would misstate a
  //      CAD payment as USD. A money envelope always carries its currency.
  //   2. otherwise the organisation's currency, via the caller's formatter -
  //      bare numbers on a currency field carry no currency of their own.
  //
  // A money OBJECT is self-identifying, so it does not need to be listed in
  // CURRENCY_FIELDS: payment_amount was absent from that list, fell through to
  // a hardcoded GBP default and rendered as pounds on a USD claim.
  if (isMoneyObject(value)) {
    const recorded = (value.currency ?? "").trim().toUpperCase();
    if (recorded) return formatCurrencyValue(value, recorded, locale);
  }

  // Unwrap the amount so we never stringify the raw dict into "[object
  // Object]". No amount yet → dash.
  if (
    currencyFmt &&
    (isMoneyObject(value) || (key && CURRENCY_FIELDS.has(key)))
  ) {
    const raw =
      value && typeof value === "object" && "amount" in value
        ? (value as { amount?: unknown }).amount
        : value;
    if (raw === null || raw === undefined || raw === "") return "-";
    const num = typeof raw === "number" ? raw : parseFloat(String(raw));
    if (!isNaN(num)) return currencyFmt(num);
  }

  if (typeof value === "boolean") return value ? "Yes" : "No";

  // File objects (resolved by backend) - show filename or "Attached"
  if (isResolvedFile(value)) return fileLabel(value, t);

  if (isMoneyObject(value)) {
    return formatCurrencyValue(value, DEFAULT_CURRENCY, locale);
  }

  // Lists (e.g. the duplicate claim references on a resolve-duplicates log)
  // render as a comma-separated string rather than "a,b" with no spacing.
  // A multi-file field (photo lists) arrives as an array of resolved file
  // objects, so name each one rather than stringifying it to "[object Object]".
  if (Array.isArray(value)) {
    if (!value.length) return "-";
    return value
      .map((item) => (isResolvedFile(item) ? fileLabel(item, t) : String(item)))
      .join(", ");
  }

  const str = String(value);

  // Detect ISO timestamps (e.g. 2026-03-31T05:02:55.244367+00:00)
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(str)) {
    const d = new Date(str);
    if (!isNaN(d.getTime())) {
      return (
        new Intl.DateTimeFormat(locale, {
          day: "numeric",
          month: "short",
          year: "numeric",
        }).format(d) +
        " at " +
        new Intl.DateTimeFormat(locale, {
          hour: "2-digit",
          minute: "2-digit",
        }).format(d)
      );
    }
  }

  // Detect date-only strings (e.g. 2026-03-25)
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const d = new Date(str + "T00:00:00");
    if (!isNaN(d.getTime())) {
      return new Intl.DateTimeFormat(locale, {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(d);
    }
  }

  // Detect snake_case / single-word enum values (e.g. paint_defect,
  // accidental_damage, reimbursed) - title-case them for display.
  if (/^[a-z][a-z0-9]*(_[a-z0-9]+)*$/.test(str)) {
    return str.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  return str;
}

export function normaliseClaimEditValue(value: unknown): unknown {
  if (isEntityFileObject(value)) {
    const nested = (value as Record<string, unknown>).value as
      Record<string, unknown> | undefined;
    const uuid = (value as Record<string, unknown>).uuid ?? nested?.uuid;
    if (typeof uuid === "string") return uuid;
  }
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (!isEntityFileObject(item)) return item;
      const nested = (item as Record<string, unknown>).value as
        Record<string, unknown> | undefined;
      const uuid = (item as Record<string, unknown>).uuid ?? nested?.uuid;
      return typeof uuid === "string" ? uuid : item;
    });
  }
  return value;
}

export function renderClaimFileDisplay(
  key: string,
  value: unknown,
): ReactNode | undefined {
  const files = Array.isArray(value)
    ? value.filter(isEntityFileObject)
    : isEntityFileObject(value)
      ? [value]
      : [];

  if (files.length === 0) return undefined;

  const isPhotoField =
    key.toLowerCase().includes("photo") || key.toLowerCase().includes("image");

  return (
    <span className="inline-flex flex-wrap justify-end gap-x-2 gap-y-1">
      {files.map((file, index) => {
        const url = entityFileDisplayUrl(file);
        const label = isPhotoField
          ? files.length === 1
            ? "Photo"
            : `Photo ${index + 1}`
          : entityFileDisplayName(file) || `File ${index + 1}`;

        return url ? (
          <a
            key={`${file.uuid ?? url}-${index}`}
            href={url}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="text-[var(--green)] underline-offset-2 hover:underline"
          >
            {label}
          </a>
        ) : (
          <span key={`${file.uuid ?? label}-${index}`}>{label}</span>
        );
      })}
    </span>
  );
}

/**
 * Build a CardFieldItem from a claim attribute key/value pair,
 * using metadata field definitions when available.
 */
export function buildClaimFieldItem(
  key: string,
  value: unknown,
  metadataMap: Map<string, ActionFieldMeta>,
  opts?: { positive?: boolean },
): CardFieldItem {
  const meta = metadataMap.get(key);
  const field: FormFieldMeta = meta
    ? normaliseClaimField(meta)
    : defineField({
        code_name: key,
        display_name: titleCaseKey(key),
        field_type: inferFieldType(key, value),
      });

  const displayValue =
    renderClaimFileDisplay(key, value) ??
    (isAddressLike(value) ? addressDisplay(value) || "-" : undefined);
  const rawValue = normaliseClaimEditValue(value);

  return {
    field,
    value: rawValue,
    displayValue,
    positive: opts?.positive,
  };
}

/**
 * Rows a card shows exactly as given and never offers for editing (WPS-203
 * AC5: the registration's owner and property on the Homeowner card).
 */
export function displayOnlyCardItems(
  rows: Array<{
    key: string;
    label: string;
    value: unknown;
    link?: boolean;
    onLinkClick?: () => void;
  }>,
): CardFieldItem[] {
  return rows.map((row) => ({
    // Namespaced so a display row never collides with a claim attribute.
    field: defineField({
      code_name: `__display_${row.key}`,
      display_name: row.label,
      field_type: "TEXT",
    }),
    value: row.value,
    // Passed through untouched, so it renders as a plain FieldRow would.
    displayValue: row.value as ReactNode,
    readOnly: true,
    link: row.link,
    onLinkClick: row.onLinkClick,
  }));
}

/**
 * A card's own rows followed by the rows a schema routes to it, with each
 * field listed once. A routed field the card already lists takes the card's
 * slot, because the routed copy is built from the schema and carries its real
 * settings (required, address lookup, default country); the rest follow in
 * routed order. Without this a field both listed and routed renders twice -
 * Property Address on the no-registration Homeowner & Property card.
 */
export function mergeRoutedCardItems(
  cardItems: CardFieldItem[],
  routedItems: CardFieldItem[],
): CardFieldItem[] {
  const routedByCode = new Map(
    routedItems.map((item) => [item.field.code_name, item]),
  );
  const cardCodes = new Set(cardItems.map((item) => item.field.code_name));
  return [
    ...cardItems.map((item) => routedByCode.get(item.field.code_name) ?? item),
    ...routedItems.filter((item) => !cardCodes.has(item.field.code_name)),
  ];
}

/**
 * An engine step (``trigger: "SYSTEM"``, e.g. the approval divert target): the
 * workflow runs it, so it is never offered as a control. The action that
 * diverts to it is.
 */
export function isEngineStep(action: { trigger?: string }): boolean {
  return action.trigger === "SYSTEM";
}

/** The actions offered as buttons: everything but the engine steps. */
export function buttonActions<T extends { trigger?: string }>(actions: T[]): T[] {
  return actions.filter((a) => !isEngineStep(a));
}

/**
 * The claim's next step for the record banner: a STATE action the workflow
 * marks primary (``ui_hints.variant``), else the lowest-order STATE action.
 * An action that leaves the claim where it is (a top-up) never is, and nor
 * is a side route the workflow marks ``next_step: false`` (send back, deny),
 * so hiding the forward actions leaves no next step rather than a wrong one.
 */
export function pickNextStep<
  T extends {
    applicability: string;
    order: number;
    primary?: boolean;
    moves_claim?: boolean;
    next_step?: boolean;
  },
>(actions: T[]): T | undefined {
  const byOrder = actions
    .filter(
      (a) =>
        a.applicability !== "GLOBAL" &&
        a.moves_claim !== false &&
        a.next_step !== false,
    )
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  return byOrder.find((a) => a.primary) ?? byOrder[0];
}

/**
 * Which LOA decisions to offer the viewer. The server leaves any action whose
 * condition this viewer fails (e.g. no ``claims.loa_actions``) out of the
 * claim's available actions, so a button whose key is missing could only fail.
 */
export function loaDecisionControls({
  availableKeys,
  approveKey,
  returnKey,
  denyKey,
  canSign,
  canDecide,
}: {
  availableKeys: ReadonlySet<string>;
  approveKey: string;
  returnKey: string;
  denyKey: string;
  canSign: boolean;
  canDecide: boolean;
}): { approve: boolean; requestChanges: boolean; deny: boolean; any: boolean } {
  const offers = (key: string) => Boolean(key) && availableKeys.has(key);
  const approve = canDecide && canSign && offers(approveKey);
  const requestChanges = canDecide && offers(returnKey);
  const deny = canDecide && offers(denyKey);
  return { approve, requestChanges, deny, any: approve || requestChanges || deny };
}
